import { RefObject } from 'react'
import { useIntl } from 'sb-react-intl'
import { useEffect, useState, useRef, useCallback, useCookies } from 'sb-hooks'


type UseCountdownValues = {
  cookieName?: string
  message: ReactIntlMessage
  nodeRef: RefObject<HTMLElement>
  expiredMessage?: ReactIntlMessage
  initialTime?: number
  withMilliseconds?: boolean // will show a count from 9 to 0, for every second
  withDoubleDigitNumber?: boolean // will show 01 instead of 1
}

type GetMessageValues = {
  intl: ReactIntl,
  message: UseCountdownValues['message'],
  timeLeft: RefObject<number>
  withMilliseconds?: UseCountdownValues['withMilliseconds']
  withDoubleDigitNumber?: UseCountdownValues['withDoubleDigitNumber']
}

const getDoubleDigitNumber = (number) => String(number).padStart(2, '0')

const getText = (values: GetMessageValues): string => {
  const { intl, message, timeLeft, withMilliseconds, withDoubleDigitNumber } = values

  const messageValues = {
    hours: Math.floor((timeLeft.current / 60 / 60 / 1000) % 60),
    minutes: Math.floor((timeLeft.current / 60 / 1000) % 60),
    seconds: Math.floor((timeLeft.current / 1000) % 60),
    milliseconds: withMilliseconds
      ? Math.floor((timeLeft.current % 1000 / 100))
      : null,
  }

  if (withDoubleDigitNumber) {
    Object.keys(messageValues).forEach((key) => {
      messageValues[key] = getDoubleDigitNumber(messageValues[key])
    })
  }

  return (
    intl.formatMessage(message, messageValues)
  )
}

const useCountdown = (values: UseCountdownValues) => {
  const {
    message, expiredMessage, nodeRef, cookieName,
    withMilliseconds, withDoubleDigitNumber, initialTime = 20 * 60 * 1000, // 20 min
  } = values

  const intl = useIntl()

  const { getCookie, setCookie } = useCookies()

  const cookieTime  = cookieName ? getCookie(cookieName) : null
  const initTime    = cookieTime || initialTime
  const tickTime    = withMilliseconds ? 100 : 1000
  const timeLeft    = useRef(initTime)
  const timer       = useRef(null)

  const [ isExpired, setExpired ] = useState(timeLeft.current <= tickTime)

  const tick = useCallback(() => {
    timeLeft.current -= tickTime

    if (timeLeft.current <= 0) {
      clearInterval(timer.current)
      setExpired(true)

      if (expiredMessage) {
        nodeRef.current.innerHTML = intl.formatMessage(expiredMessage)
      }

      return
    }

    if (cookieName) {
      setCookie(cookieName, timeLeft.current)
    }

    nodeRef.current.innerHTML = getText({
      intl,
      message,
      timeLeft,
      withMilliseconds,
      withDoubleDigitNumber,
    })
  }, [ cookieName ])

  useEffect(() => {
    if (!nodeRef || !nodeRef.current) {
      // You can not send a ref link if the timer is expired
      return
    }

    if (!isExpired) {
      timer.current = setInterval(tick, tickTime)
      tick()
    }

    return () => {
      clearInterval(timer.current)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const countdownMessage = isExpired && expiredMessage
    ? intl.formatMessage(expiredMessage)
    : getText({
      intl,
      message,
      timeLeft,
      withMilliseconds,
      withDoubleDigitNumber,
    })

  return {
    isExpired,
    countdownMessage, // for SSR render
  }
}


export default useCountdown
